import { useCallback } from 'react';
import { useKontakteHandling } from './useKontakteHandling';
import { useSnackbar } from 'notistack';
import { useT } from '../../i18n/t';

/**
 * Custom hook for downloading PDF files from Kontaktaufnahmen
 * Uses the existing useKontaktaufnahmeDownload mutation from useKontakteHandling
 * @returns A function that downloads the PDF file for a given contact
 */
export const usePdfDownload = () => {
    const { useKontaktaufnahmeDownload } = useKontakteHandling();
    const downloadMutation = useKontaktaufnahmeDownload();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useT('kontakte');

    return useCallback(async (kontaktaufnahmeIds: string | string[] | null | undefined) => {
        if (!kontaktaufnahmeIds || (Array.isArray(kontaktaufnahmeIds) && kontaktaufnahmeIds.length === 0)) {
            enqueueSnackbar(t('no-pdf-available'), { variant: 'warning' });
            return;
        }

        const ids = (Array.isArray(kontaktaufnahmeIds) ? kontaktaufnahmeIds : [kontaktaufnahmeIds])
            .filter(id => id !== null && id !== undefined && id !== '')
            .map(id => String(id));

        if (ids.length === 0) {
            enqueueSnackbar(t('no-pdf-available'), { variant: 'warning' });
            return;
        }

        try {
            await downloadMutation.mutateAsync({
                kontaktaufnahmeIds: ids
            });
        } catch (error: unknown) {
            console.error('Error downloading PDF:', error);
            const message = error instanceof Error && error.message ? error.message : t('pdf-download-error');
            enqueueSnackbar(message, { variant: 'error' });
        }
    }, [downloadMutation, enqueueSnackbar, t]);
};

